import { useEffect, useState } from 'react'
import CodeMirror from '@uiw/react-codemirror'
import { javascript } from '@codemirror/lang-javascript'
import { Save } from 'lucide-react'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'

import { parseJsArray } from '../utils'

/**
 * Raw JSON/JS array editor. Resets value when `dataKey` (the source data) changes,
 * e.g. after save when the parent refetches.
 */
export const RawTableEditor = ({
  initialValue,
  onSave,
  dataKey,
}: {
  initialValue: string
  onSave: (parsed: Record<string, unknown>[]) => Promise<void>
  dataKey: unknown[]
}) => {
  const [value, setValue] = useState(initialValue)
  const [error, setError] = useState<string | null>(null)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    setValue(initialValue)
    setError(null)
  }, [initialValue, dataKey])

  const handleSave = async () => {
    setError(null)
    try {
      const parsed = parseJsArray(value)
      if (!Array.isArray(parsed)) {
        setError('Value must be an array')
        return
      }
      const asRecords = parsed.filter((r) => r && typeof r === 'object') as Record<string, unknown>[]
      setIsSaving(true)
      await onSave(asRecords)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to parse')
    } finally {
      setIsSaving(false)
    }
  }

  return (
    <Column className="gap-4">
      <p className="text-sm text-muted-foreground">
        Edit the JavaScript array below. Use valid JSON or JS (e.g. unquoted keys). Items without id or with id ≤ 0 will be created; others updated. Removing an item deletes it on save.
      </p>
      <div className="border rounded-md overflow-hidden max-h-[400px]">
        <CodeMirror
          value={value}
          height="400px"
          extensions={[javascript({ jsx: false })]}
          onChange={(v) => {
            setValue(v)
            setError(null)
          }}
          basicSetup={{
            lineNumbers: true,
            foldGutter: true,
            highlightActiveLine: true,
            bracketMatching: true,
            closeBrackets: true,
            autocompletion: false,
          }}
          className="text-sm"
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <Row className="gap-2">
        <Button variant="primary" size="sm" onClick={handleSave} disabled={isSaving}>
          <Save className="h-4 w-4 mr-2" />
          {isSaving ? 'Saving...' : 'Apply changes'}
        </Button>
      </Row>
    </Column>
  )
}
